import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

import { ChangeTwoFactorDeviceProps } from './ChangeTwoFactorDevice.types'
import styles from './ChangeTwoFactorDevice.module.scss'
import { Button, ButtonText, Feedback, Flex, FormField, Modal, Spacer, TextInput, Typography } from '@percent/lemonade'
import { CentralCardLayout } from '@percent/domain/iam'

export function ChangeTwoFactorDevice({
  isOpened,
  onClose,
  errorMessage,
  isLoading,
  generateQRToken
}: ChangeTwoFactorDeviceProps) {
  const { t } = useTranslation()

  const formik = useFormik({
    initialValues: {
      password: ''
    },
    validationSchema: () =>
      yup.object().shape({
        password: yup.string().min(8).max(64).required('Required')
      }),
    onSubmit: ({ password }: { password: string }) => {
      generateQRToken({ password })
    }
  })

  const { errors, values, touched, dirty, isValid, handleChange, handleBlur, handleSubmit } = formik

  return (
    <Modal open={isOpened} onClose={onClose} isFullscreen>
      <div className={styles.layoutContainer}>
        <CentralCardLayout heading={t('2fa.enable2fa')} handleSubmit={handleSubmit}>
          <Typography variant="large" className={styles.headerTwo}>
            {t('2fa.enable2faTitle')}
          </Typography>
          <Spacer size={8} axis="vertical" />
          <FormikProvider value={formik}>
            <FormField
              label={t('2fa.passwordLabel')}
              status={touched.password && errors.password ? 'danger' : 'default'}
              statusMessage={errors.password}
              data-testid="password"
            >
              <TextInput
                type="password"
                name="password"
                placeholder={t('2fa.passwordPlaceholder')}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
            </FormField>
            {errorMessage && (
              <>
                <Spacer size={4} axis="vertical" />
                <Feedback variant="critical">{errorMessage}</Feedback>
              </>
            )}
            <Spacer size={6} axis="vertical" />
            <Flex direction="column" align="center" gap={24}>
              <Button
                type="submit"
                stretch
                size="large"
                disabled={!(isValid && dirty)}
                loading={isLoading}
                data-testid="setup-button"
              >
                {t('button.setUp')}
              </Button>
              <ButtonText onClick={onClose}>{t('button.cancel')}</ButtonText>
            </Flex>
          </FormikProvider>
        </CentralCardLayout>
      </div>
    </Modal>
  )
}
